import { Injectable, computed, inject } from '@angular/core';
import { IMenuItem } from '../models/menu.model';
import { UserMenuService } from 'projects/shared/src/lib/services/user-menu.service';
import { Title } from '@angular/platform-browser';
import { EVFunctions } from 'ets-fe-ng-sdk';
import { Config } from '../configs/index.config';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';
import { EUserType } from '../@ngrx/user/user.model';
import { setupMenu } from '../configs/menu-configs/setup.menu';
import { publicPortalMenu } from '../configs/menu-configs/candidate.menu';
import { superAdminMenu } from '../configs/menu-configs/super-admin.menu';
import { policyValdidationMenu } from '../configs/menu-configs/policy.menu';
import { productMenu } from '../configs/menu-configs/product.menu';
import { supervisionMenu } from '../configs/menu-configs/supervision.menu';
import { complianceMenu } from '../configs/menu-configs/compliance.menu';
import { lossAdjusterMenu } from '../configs/menu-configs/loss-adjuster.menu';
import { visitorDeskMenu } from '../configs/menu-configs/visitor.menu';
import { commissionerMenu } from '../configs/menu-configs/commissioner.menu';
import { listsDeskMenu } from '../configs/menu-configs/lists-desk.menu';
import { industryDataMenu } from '../configs/menu-configs/industry-data.menu';

@Injectable({ providedIn: 'root' })
export class AppService {
  readonly userMenuS = inject(UserMenuService);
  pageTitle?: string;
  readonly appName = computed(() => {
    const ut = this.authS.userType();
    if (!ut) return '';
    if (ut === EUserType.admin) return 'Insurance Regulatory Platform';
    return `${ut === EUserType.company ? `Insurers'` : ut} Portal`;
  });

  formatMenu(menuList: IMenuItem[], parent?: IMenuItem) {
    let cMenuList: IMenuItem[];
    if (menuList) {
      cMenuList = [];
      for (const { children, ...item } of menuList) {
        let cItem: IMenuItem = {};
        Object.assign(cItem, item);
        // debugger;
        const d = Object.isExtensible(cItem);
        cItem.fullPath = cItem.path;
        if (parent?.fullPath) cItem.fullPath = `${parent.fullPath}/${cItem.path || ''}`;
        if (children?.length) cItem.children = this.formatMenu(children, cItem);
        cMenuList.push(cItem);
      }
    }
    return cMenuList!;
  }

  private readonly intermediaryDeskMenu = computed<IMenuItem[]>(() =>
    this.authS.canAccessIntermediary()
      ? [
        {
          label: `Intermediary Desk`,
          path: `intermediary-desk`,
          id: 'MID_INTERMEDIARY_DESK',
          icon: 'fa fa-users',
          children: [
            // { path: `tied-agent-initiation`, label: `Tied Agent Initiation` },
            // { path: `register-tied-agent`, label: `Register Tied Agent` },
            {
              path: `tied-agents`,
              label: `Tied Agents`,
              id: 'MID_TIED_AGENTS',
              children: [
                { path: `register`, label: `Register Tied Agent`, id: 'MID_REGISTER_TIED_AGENT' },
                { path: 'find', label: 'Maintain Tied Agents', id: 'MID_MAINTAIN_TIED_AGENTS' },
                ...(!environment.isAdmin || true
                  ? [
                    { path: `accounts/naicom`, label: `NAICOM Accounts`, id: 'MID_NAICOM_ACCOUNTS' },
                    { path: `accounts/cifm`, label: `CIFM Accounts`, id: 'MID_CIFM_ACCOUNTS' },
                    { path: `accounts/arian`, label: `ARIAN Accounts`, id: 'MID_ARIAN_ACCOUNTS' },
                  ]
                  : []),
                { path: `analysis`, label: `Analysis`, id: 'MID_TIED_AGENTS_ANALYSIS' },
              ],
            },
            {
              path: `referral-agents`,
              label: `Referral Agents`,
              id: 'MID_REFERRAL_AGENTS',
              children: [
                { path: `register`, label: `Register Referral Agent`, id: 'MID_REGISTER_REFERRAL_AGENT' },
                { path: `find`, label: `Maintain Referral Agent`, id: 'MID_MAINTAIN_REFERRAL_AGENT' },
              ],
            },
            {
              path: `broker-agents`,
              label: `Broker`,
              id: 'MID_BROKER',
              children: [
                // { path: `reinsurance-application`, label: `Broker License Application` },
                { path: `maintain`, label: `Broker Register`, id: 'MID_BROKER_REGISTER' },
                // { path: `infractions`, label: `Infractions`, id: 'MID_INFRACTIONS' },
                { path: `credit-notes`, label: `Credit Notes`, id: 'MID_CREDIT_NOTES' },
                { path: `coinsurance`, label: `Coinsurance`, id: 'MID_COINSURANCE' },
              ],
            },
            {
              path: `broker-loss-adjuster`,
              label: `Broker Loss Adjuster`,
              id: 'MID_BROKER_LOSS_ADJUSTER',
            },
          ],
        },
      ]
      : [],
  );

  private readonly ciinMenu = computed<IMenuItem[]>(() =>
    this.authS.canAccessCIIN()
      ? [
        {
          path: 'ciin',
          label: 'CIIN',
          id: 'MID_CIIN',
          icon: 'fa fa-building',
          children: [
            { path: 'set-pins', label: 'Set Pins', id: 'MID_SET_PINS' },
            { path: 'candidates', label: 'Find Candidates', id: 'MID_FIND_CANDIDATES' },
            { path: `accounts`, label: `Accounts`, id: 'MID_CIIN_ACCOUNTS' },

            { path: `accounts/cifm`, label: `CIFM Accounts`, id: 'MID_CIIN_CIFM_ACCOUNTS' },
          ],
        },
      ]
      : [],
  );

  private readonly bppMenu = computed<IMenuItem[]>(() => [
    {
      path: 'policy-desk',
      label: 'Policy Desk',
      id: 'MID_BPP_POLICY_DESK',
      icon: 'fa fa-file-contract',
      children: policyValdidationMenu,
    },
    {
      path: 'set-ups',
      label: 'Admin Desk',
      id: 'MID_BPP_ADMIN',
      icon: 'fa fa-building-user',
        children: [
          { path: 'users', label: 'Users', id: 'MID_BPP_ADMIN_USERS' },
        ],
      },
  ]);

  readonly menuFirstPathGetter = (menu: IMenuItem[], parentPath?: string): string => {
    if (!menu?.length) return '';
    const path = menu[0].path;
    const nPath = parentPath ? `${parentPath}/${path || ''}` : path || '';
    if (menu[0].children?.length) return this.menuFirstPathGetter(menu[0].children, nPath);
    return nPath;
  };

  readonly bppMenuFirstPath = computed(() => this.menuFirstPathGetter(this.bppMenu()));

  private readonly _cifmMenu = computed<IMenuItem[]>(() =>
    this.authS.canAccessCIFM()
      ? [
        {
          path: `cifm`,
          label: `CIFM`,
          id: 'MID_CIFM',
          icon: 'fa fa-users-gear',
          children: [
            // { path: `find-certificate`, label: `Find Certificate` },
            { path: `candidates`, id: 'MID_CIFM_CANDIDATES', label: `Candidates` },
            // {
            // path: `tied-agents`,
            // label: `Tied Agents`,
            // children: [
            { path: 'tied-agents/find', label: 'Tied Agents', id: 'MID_CIFM_TIED_AGENTS' },
            // ],
            // },
            { path: `exam/results`, label: `Examination Results`, id: 'MID_EXAM_RESULTS' },
            { path: `exam/setup`, label: `Examination Setup`, id: 'MID_EXAM_SETUP' },
            { path: `exam/bank`, label: `Examination Bank`, id: 'MID_EXAM_BANK' },
            // { path: `take-exam`, label: `Take Exam` },
            { path: `courses`, label: `Courses`, id: 'MID_COURSES' },
            { path: `accounts/cifm`, label: `CIFM Accounts`, id: 'MID_CIFM_ACCOUNTS_MAIN' },
            ...(this.authS.isCiin() || this.authS.isCifm() || this.authS.isAdmin()
              ? [{ path: `accounts/ciin`, label: `CIIN Accounts`, id: 'MID_CIFM_CIIN_ACCOUNTS' }]
              : []),
          ],
        },
      ]
      : [],
  );

  private readonly cifmMenu = computed<IMenuItem[]>(() => [
    ...this._cifmMenu(),
    ...this.intermediaryDeskMenu(),
    ...this.ciinMenu(),
    ...this.arianMenu(),
    // ...this.bppMenu(),
    {
      label: `Set Ups`,
      path: `set-ups`,
      id: 'MID_SET_UPS',
      icon: 'fa fa-cogs',
      children: [
        // {
        //   path: `parameters`,
        //   label: `Parameters`,
        // },
        ...(this.authS.isCIFMOrAdmin()
          ? <IMenuItem[]>[{ path: `companies`, label: `Companies`, id: 'MID_COMPANIES' }]
          : []),
        { path: `agent-types`, label: `Agent Types`, id: 'MID_AGENT_TYPES' },
        { path: `users`, label: `Users`, id: 'MID_USERS' },
        // { path: `user-groups`, label: `User Groups` },
      ],
    },
    {
      label: `Admin`,
      path: `admin-desk`,
      id: 'MID_ADMIN_DESK',
      icon: 'fa fa-building-user',
      children: [
        {
          label: 'Agent',
          path: 'agent',
          id: 'MID_AGENT_MGT',
          children: [
            { label: 'Fraud Investigation', path: 'fraud-investigation', id: 'MID_FRAUD_INVESTIGATION' },
            {
              label: 'Migration',
              path: 'migration',
              id: 'MID_MIGRATION',
              children: [
                ...(this.authS.isCifm()
                  ? <IMenuItem[]>[{ label: 'Staged Agents', path: 'staging', id: 'MID_STAGING' }]
                  : []),

                { label: 'Candidate RIA', path: 'candidate-ria', id: 'MID_CANDIDATE_RIA' },
                { label: 'Create Legacy Agent', path: 'create-legacy', id: 'MID_CREATE_LEGACY' },
              ],
            },
          ],
        },
        { label: 'Bill Reference Checker', path: 'rrr/checker', id: 'MID_RRR_CHECKER' },
        { label: 'Batch Operations', path: 'batch', id: 'MID_BATCH_OPERATIONS' },
        { label: `User Activity`, path: 'user/activity', id: 'MID_USER_ACTIVITY' },
        { label: 'Documents', path: 'documents', id: 'MID_DOCUMENTS' },
      ],
    },
  ]);
  private menuDestructurer = (menu: IMenuItem) => {
    const children = menu.children?.sort3('label');
    return {
      ...menu,
      children: children
        ?.filter((a) => a.children?.length)
        ?.concat(children?.filter((a) => !a.children?.length)),
    };
  };

  readonly adminMenu = computed<IMenuItem[]>(() => {
    const intermediary = this.intermediaryDeskMenu(),
      _cifmMenu = this._cifmMenu(),
      ciinMenu = this.ciinMenu(),
      arianMenu = environment.isAdmin ? this.arianMenu() : [];

    if (intermediary?.[0])
      intermediary?.[0].children?.push(
        ..._cifmMenu.map(({ icon, ...x }) => x),
        ...ciinMenu.map(({ icon, ...x }) => x),
        ...arianMenu.map(({ icon, ...x }) => x),
      );

    return [
      { label: 'Dashboard', path: 'dashboard', id: 'MID_DASHBOARD', icon: 'fa fa-home' },
      ...intermediary,
      // ...this.bppMenu(),
      this.menuDestructurer(commissionerMenu),
      {
        ...industryDataMenu,
        children: industryDataMenu.children?.map(({ icon, ...menu }) => this.menuDestructurer(menu)),
      },
      this.menuDestructurer(productMenu),
      this.menuDestructurer(supervisionMenu),
      this.menuDestructurer(complianceMenu),
      this.menuDestructurer(lossAdjusterMenu),
      this.menuDestructurer(visitorDeskMenu),
      {
        label: 'CRM',
        path: 'crm-desk',
        id: 'MID_CRM',
        icon: 'fa fa-people-group',
        children: this.crmMenu().map(({ icon, ...x }) => x),
      },
      {
        label: `Workflow `,
        path: `workflow-desk`,
        id: 'MID_WORKFLOW',
        icon: 'fa fa-tasks',
        children: [
          { label: 'Overview', path: 'overview', id: 'MO29' },
          { label: 'Workflow Task', path: 'task', id: 'MID_WORKFLOW_TASK' },
          { label: 'Workflow Schedule', path: 'schedule', id: 'MID_WORKFLOW_SCHEDULE' },
          { label: 'Workflow Queries', path: 'queries', id: 'MID_WORKFLOW_QUERIES' },
          // {
          //   label: 'Create New',
          //   children: [
          //     {
          //       label: 'Tasks',
          //       path: 'task/create',
          //     },
          //     {
          //       label: 'Schedules',
          //       path: 'schedule/create',
          //     },
          //   ],
          // },
        ],
      },
      this.menuDestructurer(setupMenu),
      this.menuDestructurer(listsDeskMenu),
      {
        label: `Reports`,
        path: `report-desk`,
        id: 'MID_REPORTS',
        icon: 'fa fa-note-sticky',
        children: [
          { label: 'Accounts', path: 'accounts', id: 'MID_REPORT_ACCOUNTS' },
          { label: 'Agents', path: 'agents', id: 'MID_REPORT_AGENTS' },
          { label: 'Bond Policies', path: 'bond-policies', id: 'MID_REPORT_BOND_POLICIES' },
          { label: 'Claim Summary', path: 'claim-summary', id: 'MID_REPORT_CLAIM_SUMMARY' },
          {
            label: 'Annuity New Business',
            path: 'annuity-new-business',
            id: 'MID_REPORT_ANNUITY_NEW_BUSINESS',
          },
          {
            label: 'Net Exposures',
            path: 'net-exposures',
            id: 'MID_REPORT_NET_EXPUSURES',
          },
          {
            label: 'Analytics',
            id: 'MID_REPORT_ANALYTICS',
            children: [
              {
                label: 'Financials',
                id: 'MID_REPORT_ANALYTICS_FINANCIALS',
                children: [
                  {
                    label: 'Sectorial Performance',
                    path: 'sectorial-performance',
                    id: 'MID_REPORT_ANALYTICS_FINANCIALS_SECTORIAL_PERFORMANCE',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        label: `Admin`,
        path: `admin-desk`,
        id: 'MID_ADMIN_DESK_ADMIN',
        icon: 'fa fa-building-user',
        children: [
          {
            label: 'Migrations',
            path: 'migrations',
            id: 'MID_ADMIN_MIGRATIONS',
            children: [
              { label: 'Product Migration', path: 'product', id: 'MID_ADMIN_PRODUCT_MIGRATION' },
              {
                label: 'Product Benefits Migration',
                path: 'product-benefits',
                id: 'MID_ADMIN_PRODUCT_BENEFITS_MIGRATION',
              },
            ],
          },
          {
            label: 'Agent',
            path: 'agent',
            id: 'MID_ADMIN_AGENT',
            children: [
              { label: 'Activation', path: 'activation', id: 'MID_ADMIN_ACTIVATION' },
              {
                label: 'Migration',
                path: 'migration',
                id: 'MID_ADMIN_MIGRATION',
                children: [
                  { label: 'Agents', path: 'admin', id: 'MID_ADMIN_MIGRATION_AGENTS' },
                  // {
                  //   label: 'Staged Agents',
                  //   path: 'staging',
                  // },
                  // {
                  //   label: 'Candidate RIA',
                  //   path: 'candidate-ria',
                  // },
                  { label: 'Create Legacy Agent', path: 'create-legacy', id: 'MID_ADMIN_CREATE_LEGACY' },
                ],
              },

              {
                label: 'Fraud Investigation',
                path: 'fraud-investigation',
                id: 'MID_ADMIN_FRAUD_INVESTIGATION',
              },
            ],
          },
          {
            label: 'Broker',
            path: 'broker',
            id: 'MID_ADMIN_BROKER',
            children: [{ path: `migration`, label: `Broker Migration`, id: 'MID_ADMIN_BROKER_MIGRATION' }],
          },
          { label: 'Bill Reference Checker', path: 'rrr/checker', id: 'MID_ADMIN_RRR_CHECKER' },
          { label: 'Batch Operations', path: 'batch', id: 'MID_ADMIN_BATCH_OPERATIONS' },
          { label: `User Activity`, path: 'user/activity', id: 'MID_ADMIN_USER_ACTIVITY' },
          // {
          //   label: 'Documents',
          //   path: 'documents',
          // },
          {
            label: 'Help',
            path: 'help/save',
            id: 'MID_HELP',
            children: [
              { label: 'Admin', path: 'admin', id: 'MID_HELP_ADMIN' },
              { label: 'Candidate', path: 'candidate', id: 'MID_HELP_CANDIDATE' },
            ],
          },
        ],
      },
    ];
  });

  private readonly crmMenu = computed<IMenuItem[]>(() => {
    return [
      { label: `Dashboard`, path: 'dashboard', id: 'MID_CRM_DASHBOARD', icon: 'fa fa-home' },
      {
        label: `Application Desk`,
        path: 'application-desk',
        id: 'MID_CRM_APPLICATION_DESK',
        icon: 'fa fa-file-alt',
        children: [
          { label: 'All Applications', path: 'all-applications', id: 'MID_CRM_FIND_APPLICATION_DESK' },
          { label: 'Approved Applications', path: 'approved-applications', id: 'MID_CRM_A_APPLICATIONS' },
        ],
      },
      { label: `Applicants`, path: 'applicants', id: 'MID_CRM_APPLICANTS', icon: 'fa fa-users' },
      // { label: `Advert License`, path: 'advert-license', id: 'MID_CRM_ADVERT_LICENSE', icon: 'fa fa-ad' },
      { label: 'Documents', path: 'admin/documents', id: 'MID_CRM_DOCUMENTS', icon: 'fa fa-folder' },
      {
        label: `Complaints Desk`,
        path: 'complaints-desk',
        id: 'MID_CRM_COMPLAINTS',
        icon: 'fa fa-exclamation-circle',
        children: [{ label: 'All Complaints', path: 'all-complaints', id: 'MID_CRM_FIND_COMPLAINTS' }],
      },
      {
        label: 'Professional Register',
        path: 'professional-register',
        id: 'MID_CRM_PROFESSIONAL_REGISTER',
        icon: 'fa fa-id-card',
      },
      {
        label: 'Company Professional Register',
        path: 'company-professional-register',
        id: 'MID_CRM_COMPANY_PROFESSIONAL_REGISTER',
        icon: 'fa fa-id-card',
      },
    ];
  });

  private readonly arianMenu = computed<IMenuItem[]>(() =>
    this.authS.canAccessARIAN()
      ? [
        {
          path: 'arian',
          label: 'ARIAN',
          id: 'MID_ARIAN',
          icon: 'fa fa-building',
          children: [
            { path: 'setup', label: 'ARIAN Set up', id: 'MID_ARIAN_SETUP', icon: 'fa fa-cogs' },
            {
              path: `accounts`,
              label: `Accounts`,
              id: 'MID_ARIAN_ACCOUNTS_MAIN',
              icon: 'fa fa-money-bills',
            },
            {
              path: 'candidates',
              label: 'Find Candidates',
              id: 'MID_ARIAN_FIND_CANDIDATES',
              icon: 'fa fa-users',
            },
          ],
        },
      ]
      : [],
  );

  private checkDuplicateIds(menuList: IMenuItem[], seenIds: Set<string> = new Set()) {
    for (const item of menuList) {
      if (item.id) {
        if (seenIds.has(item.id)) {
          throw new Error(`Duplicate menu ID found: ${item.id} for item: ${item.label}`);
        }
        seenIds.add(item.id);
      }
      if (item.children?.length) {
        this.checkDuplicateIds(item.children, seenIds);
      }
    }
  }

  readonly unfilteredSystemMenu = computed<IMenuItem[]>(() => {
    const adminMenu = this.adminMenu(),
      cifmMenu = this.cifmMenu(),
      crmMenu = this.crmMenu(),
      bppMenu = this.bppMenu(),
      candMenu = this.authS.publicPortalIdentityReady()
        ? publicPortalMenu(this.authS.publicPortalPersona(), this.authS.publicPortalSection())
        : [],
      isSuperAdmin = this.authS.isSuperAdmin();
    // debugger;
    const res = this.formatMenu(
      isSuperAdmin
        ? superAdminMenu.map(this.menuDestructurer).sort3('label')
        : environment.isAdmin || this.authS.user()?.userMenu
          ? adminMenu
          : environment.isCRM
            ? crmMenu
            : this.authS.isBpp()
              ? bppMenu
              : environment.isPartner
                ? cifmMenu
                : environment.isCandidate
                  ? candMenu
                  : [],
    );
    if (!isSuperAdmin)
      this.checkDuplicateIds(res);
    return res;
  });

  readonly systemMenu = computed<IMenuItem[]>(() => {
    const unfiltered = this.unfilteredSystemMenu();
    if (this.authS.isSuperAdmin()) return unfiltered;
    const menuDetails = this.userMenuS.userMenuDetails();
    if (!menuDetails?.length) return unfiltered;

    const filterMenu = (menu: IMenuItem[]): IMenuItem[] => {
      return menu
        .filter((item) => {
          const detail = menuDetails.find(
            (d) => d.slug === item.id || d.slug === item.path || d.slug === item.fullPath,
          );
          return detail ? detail.viewAccess : true; // If not in list, assume accessible for now or default to false?
          // Re-reading request: "hide the menus based on what is assigned to the user"
          // If it's a dynamic system, usually if it's NOT in the details, it SHOULD be hidden if we are using an opt-in model.
          // However, some core menus like "Dashboard" might not be in the list.
          // Let's stick to: if it's in the list, respect viewAccess. If NOT in the list, it's visible (default).
          // Actually, typically it's the other way around. But let's see how the selection works.
        })
        .map((item) => ({ ...item, children: item.children ? filterMenu(item.children) : [] }))
        .filter((item) => item.viewAccess !== false); // Double check
    };

    // Refined filtering logic:
    const slugs = new Set(menuDetails.filter((d) => d.viewAccess).map((d) => d.slug));
    const filter = (list: IMenuItem[]): IMenuItem[] => {
      return list.reduce((acc, item) => {
        const isAccessible = slugs.has(item.id!) || slugs.has(item.path!) || slugs.has(item.fullPath!);
        const filteredChildren = item.children ? filter(item.children) : [];

        if (isAccessible || filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        }
        return acc;
      }, [] as IMenuItem[]);
    };

    return filter(unfiltered);
  });

  constructor(
    public title: Title,
    public authS: AuthenticationService,
  ) { }

  setTitle(title: string) {
    this.pageTitle = title;
    this.title.setTitle(EVFunctions.strConcatenator2(title, Config.appName, ' | '));
  }

  turnOffScreenCapture() {
    document.getElementsByTagName('body')[0].classList.remove('no-scrsht');
  }

  turnOnScreenCapture() {
    document.getElementsByTagName('body')[0].classList.add('no-scrsht');
  }
}
